import Vue from 'vue'
import { GET_REQUEST_ORGANIZATION, SAVE_REQUEST_ORGANIZATION } from './requests'

export const currentActor = () => ({
  customerId: localStorage.getItem('userId'),
  token: localStorage.getItem('userToken')
})

export function createOrganizationFlow (read, write, actor) {
  const state = Vue.observable({
    data: null,
    loading: false,
    saving: false,
    documentPending: false,
    error: '',
    promptOpen: false,
    selected: '',
    actorVersion: 0
  })
  let identity = ''
  let credentials = null
  let readTask = null
  let intent = null
  let documentFlight = null
  let promptVersion = 0
  let dismissed = false
  let needsRefresh = false

  function key (auth) {
    return auth.customerId && auth.token ? JSON.stringify([auth.customerId, auth.token]) : ''
  }

  function cancelPrompt () {
    promptVersion++
    dismissed = true
    state.promptOpen = false
    state.selected = ''
    if (intent && intent.resolve) intent.resolve(false)
    intent = null
    // A dismissed write can have succeeded remotely. The next safe action rereads.
    if (state.saving) state.data = null
  }

  function syncActor (automatic = true) {
    const auth = actor()
    const next = key(auth)
    if (next === identity) return
    cancelPrompt()
    identity = next
    credentials = next ? auth : null
    state.actorVersion++
    state.data = null
    state.error = ''
    state.documentPending = false
    documentFlight = null
    state.loading = false
    state.saving = false
    readTask = null
    dismissed = false
    needsRefresh = false
    if (next && automatic) refresh().catch(() => {})
  }

  function valid (ticket) {
    syncActor(false)
    return !!ticket && ticket === intent && ticket.version === state.actorVersion && !!credentials
  }

  function report (error) {
    state.error = error.message
    if (error.authFailure) {
      cancelPrompt()
      state.actorVersion++
      state.data = null
      state.loading = false
      state.saving = false
      readTask = null
    }
  }

  async function refresh () {
    syncActor(false)
    if (!credentials) return null
    if (readTask) return readTask
    const version = state.actorVersion
    const auth = credentials
    state.loading = true
    state.error = ''
    const task = (async () => {
      try {
        const data = await read(auth)
        syncActor(false)
        if (version !== state.actorVersion) return null
        state.data = data
        needsRefresh = false
        if (data.is_employee && data.organization_id === null && !dismissed) {
          state.selected = ''
          state.promptOpen = true
        }
        return data
      } catch (error) {
        syncActor(false)
        if (version === state.actorVersion) report(error)
        throw error
      } finally {
        if (version === state.actorVersion) {
          state.loading = false
          readTask = null
        }
      }
    })()
    readTask = task
    return task
  }

  function begin (owner) {
    syncActor(false)
    if (!credentials || intent || state.saving || state.documentPending) return null
    intent = { owner, version: state.actorVersion }
    return intent
  }

  async function ensure (ticket) {
    const data = await refresh()
    if (!valid(ticket) || !data) return false
    if (!data.is_employee || data.organization_id !== null) return true
    state.selected = ''
    state.promptOpen = true
    return new Promise(resolve => { ticket.resolve = resolve })
  }

  function markSending (ticket) {
    if (!valid(ticket) || state.documentPending) return false
    documentFlight = ticket
    state.documentPending = true
    return true
  }

  function finish (ticket) {
    if (intent === ticket) intent = null
    if (documentFlight === ticket) {
      documentFlight = null
      state.documentPending = false
    }
  }

  function leaveScreen () {
    if (intent || state.promptOpen) cancelPrompt()
  }

  function cancelOwned (owner) {
    if (intent && intent.owner === owner) cancelPrompt()
  }

  async function save (id, mode) {
    syncActor(false)
    if (!credentials || state.saving) return false
    state.saving = true
    state.error = ''
    const version = state.actorVersion
    const prompt = promptVersion
    try {
      if (needsRefresh || !state.data) await refresh()
      if (version !== state.actorVersion || prompt !== promptVersion || !state.data || !state.data.is_employee ||
          !state.data.organizations.some(item => String(item.id) === String(id))) return false
      const data = await write(credentials, { organization_id: Number(id), mode })
      syncActor(false)
      if (version !== state.actorVersion || prompt !== promptVersion) return false
      state.data = data
      return true
    } catch (error) {
      syncActor(false)
      if (version === state.actorVersion && prompt === promptVersion) {
        needsRefresh = true
        report(error)
      }
      return false
    } finally {
      if (version === state.actorVersion) state.saving = false
    }
  }

  async function confirm () {
    if (!state.promptOpen) return false
    const saved = await save(state.selected, 'initialize')
    if (!saved || !state.data || state.data.organization_id === null) return false
    state.promptOpen = false
    state.selected = ''
    dismissed = true
    if (intent && intent.resolve) intent.resolve(true)
    return true
  }

  function documentError (error) {
    syncActor(false)
    if (error.authFailure) report(error)
  }

  return {
    state,
    syncActor,
    refresh,
    begin,
    ensure,
    valid,
    markSending,
    finish,
    cancelPrompt,
    cancelOwned,
    leaveScreen,
    confirm,
    replace: id => save(id, 'replace'),
    documentError
  }
}

export const organizationFlow = createOrganizationFlow(
  GET_REQUEST_ORGANIZATION, SAVE_REQUEST_ORGANIZATION, currentActor
)
